import { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../../utils/axiosInstance";
import { AuthContext } from "../../../context/AuthContext";
import { isFreelance } from "../../../utils/constants/accountType";
import {
  FiUsers,
  FiBriefcase,
  FiFolder,
  FiGrid,
  FiLayers,
  FiArrowUpRight,
} from "react-icons/fi";

/* ── Stat card colours ── */
const PALETTE = {
  blue:    { icon: "bg-blue-50 text-blue-600",       border: "border-t-blue-500",    ring: "group-hover:ring-blue-200"    },
  violet:  { icon: "bg-violet-50 text-violet-600",   border: "border-t-violet-500",  ring: "group-hover:ring-violet-200"  },
  indigo:  { icon: "bg-indigo-50 text-indigo-600",   border: "border-t-indigo-500",  ring: "group-hover:ring-indigo-200"  },
  cyan:    { icon: "bg-cyan-50 text-cyan-600",       border: "border-t-cyan-500",    ring: "group-hover:ring-cyan-200"    },
  emerald: { icon: "bg-emerald-50 text-emerald-600", border: "border-t-emerald-500", ring: "group-hover:ring-emerald-200" },
  amber:   { icon: "bg-amber-50 text-amber-600",     border: "border-t-amber-500",   ring: "group-hover:ring-amber-200"   },
};

const formatCount = (n) => {
  if (n === null || n === undefined) return null;
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
};

/* ── Vertical stat card ── */
const StatCard = ({ icon: Icon, label, value, color = "blue", to, loading }) => {
  const c = PALETTE[color] ?? PALETTE.blue;
  const formatted = formatCount(value);
  const card = (
    <div className={`group relative bg-white rounded-xl border border-gray-100 border-t-2 ${c.border}
      shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 p-5 flex flex-col gap-3`}>
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ring-2 ring-transparent transition ${c.icon} ${c.ring}`}>
          <Icon size={18} />
        </div>
        {to && (
          <FiArrowUpRight
            size={14}
            className="text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition"
          />
        )}
      </div>
      <div>
        {loading ? (
          <div className="h-7 w-12 bg-gray-100 rounded animate-pulse" />
        ) : (
          <p className="text-2xl font-bold text-gray-800 leading-none">
            {formatted ?? <span className="text-gray-300">0</span>}
          </p>
        )}
        <p className="text-sm text-gray-500 mt-1.5">{label}</p>
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{card}</Link> : card;
};

/* ── Status badges ── */
const JOB_BADGE = {
  Draft:                "bg-gray-100 text-gray-600",
  "Offered to Vendor":  "bg-blue-100 text-blue-700",
  "Offer Accepted":     "bg-indigo-100 text-indigo-700",
  "Offer Rejected":     "bg-red-100 text-red-700",
  Started:              "bg-yellow-100 text-yellow-700",
  Completed:            "bg-green-100 text-green-700",
  "Completion Accepted":"bg-emerald-100 text-emerald-700",
  "Completion Rejected":"bg-rose-100 text-rose-700",
  Hold:                 "bg-orange-100 text-orange-700",
  Cancelled:            "bg-gray-200 text-gray-500",
};

const PROJECT_BADGE = {
  "In Progress":         "bg-blue-100 text-blue-700",
  Draft:                 "bg-gray-100 text-gray-600",
  Submitted:             "bg-indigo-100 text-indigo-700",
  "Submission Accepted": "bg-green-100 text-green-700",
  "Submission Rejected": "bg-red-100 text-red-700",
  "Offered by Client":   "bg-purple-100 text-purple-700",
  "Offer Accepted":      "bg-teal-100 text-teal-700",
  "Offer Rejected":      "bg-rose-100 text-rose-700",
  Hold:                  "bg-orange-100 text-orange-700",
  Cancelled:             "bg-gray-200 text-gray-500",
};

const Badge = ({ status, map }) => (
  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[status] ?? "bg-gray-100 text-gray-600"}`}>
    {status}
  </span>
);

/* ── Section card wrapper ── */
const Card = ({ title, action, children }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-50">
      <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h2>
      {action}
    </div>
    <div className="px-6 py-4">{children}</div>
  </div>
);

/* ── Main ── */
const AdminDashboard = () => {
  const { user } = useContext(AuthContext);
  // Freelance accounts have no managers, vendors, client pools or jobs — hide
  // those cards/actions and skip their (enterprise-only) endpoints.
  const freelance = isFreelance(user);
  const [counts, setCounts] = useState({ managers: null, clients: null, vendors: null, clientPools: null, projects: null, jobs: null });
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [jobBreakdown, setJobBreakdown] = useState({});
  const [projectBreakdown, setProjectBreakdown] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const countOf = (res) =>
      res?.data?.meta?.total ?? (Array.isArray(res?.data?.data) ? res.data.data.length : 0);

    // Enterprise-only endpoints are skipped entirely for freelance accounts.
    const skip = Promise.resolve(null);

    const fetchAll = async () => {
      try {
        const [managersRes, clientsRes, vendorsRes, clientPoolsRes, projectsRes, jobsRes] =
          await Promise.all([
            freelance ? skip : api.get("/managers"),
            api.get("/clients"),
            freelance ? skip : api.get("/vendors"),
            freelance ? skip : api.get("/client-pools"),
            api.get("/projects?limit=5"),
            freelance ? skip : api.get("/jobs?limit=5"),
          ]);

        setCounts({
          managers:    managersRes ? countOf(managersRes) : null,
          clients:     countOf(clientsRes),
          vendors:     vendorsRes ? countOf(vendorsRes) : null,
          clientPools: clientPoolsRes ? countOf(clientPoolsRes) : null,
          projects:    countOf(projectsRes),
          jobs:        jobsRes ? countOf(jobsRes) : null,
        });

        const projects = projectsRes.data?.data ?? [];
        setRecentProjects(projects);
        const pb = {};
        projects.forEach((p) => { pb[p.status] = (pb[p.status] ?? 0) + 1; });
        setProjectBreakdown(pb);

        const jobs = jobsRes?.data?.data ?? [];
        setRecentJobs(jobs);
        const jb = {};
        jobs.forEach((j) => { jb[j.status] = (jb[j.status] ?? 0) + 1; });
        setJobBreakdown(jb);
      } catch (err) {
        console.error("AdminDashboard fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [freelance]);

  const tableHeaderCls = "text-left text-xs font-semibold text-gray-400 uppercase tracking-wider pb-2 border-b border-gray-100";
  const skeletonRows = [...Array(3)].map((_, i) => (
    <tr key={i}><td colSpan={3} className="py-2"><div className="h-8 bg-gray-100 rounded-lg animate-pulse" /></td></tr>
  ));

  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const displayName = user?.username || user?.email?.split("@")[0] || "Admin";
  const today = new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 rounded-2xl px-7 py-6 text-white shadow-md">
        <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full bg-white/10 blur-2xl pointer-events-none" />
        <div className="absolute -right-20 bottom-0 w-64 h-64 rounded-full bg-indigo-400/20 blur-3xl pointer-events-none" />
        <div className="relative">
          <p className="text-xs font-semibold tracking-widest uppercase text-blue-200 mb-1">{today}</p>
          <h1 className="text-2xl font-bold tracking-tight">{greeting}, {displayName}.</h1>
          <p className="text-sm text-blue-200 mt-1">Here's what's happening across your workspace today.</p>
        </div>
      </div>

      {/* ── Stat cards ── */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-4">
        {!freelance && (
          <StatCard icon={FiUsers}    label="Managers"     value={counts.managers}    color="blue"    to="/managers"     loading={loading} />
        )}
        <StatCard icon={FiUsers}    label="Clients"      value={counts.clients}     color="violet"  to="/clients"      loading={loading} />
        {!freelance && (
          <>
            <StatCard icon={FiUsers}    label="Vendors"      value={counts.vendors}     color="indigo"  to="/vendors"      loading={loading} />
            <StatCard icon={FiLayers}   label="Client Pools" value={counts.clientPools} color="cyan"    to="/client-pools" loading={loading} />
          </>
        )}
        <StatCard icon={FiFolder}   label="Projects"     value={counts.projects}    color="emerald" to="/projects"     loading={loading} />
        {!freelance && (
          <StatCard icon={FiBriefcase}label="Jobs"         value={counts.jobs}        color="amber"   to="/jobs"         loading={loading} />
        )}
      </div>

      {/* ── Recent activity ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">

        <Card
          title="Recent Projects"
          action={<Link to="/projects" className="text-sm text-blue-600 hover:underline font-medium">View all</Link>}
        >
          {loading ? (
            <table className="w-full"><tbody>{skeletonRows}</tbody></table>
          ) : recentProjects.length === 0 ? (
            <p className="text-sm text-gray-400">No projects yet.</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className={tableHeaderCls}>Project</th>
                  <th className={tableHeaderCls}>Client</th>
                  <th className={tableHeaderCls}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentProjects.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2.5 pr-4">
                      <Link to={`/project/${p.id}`} className="text-sm font-medium text-gray-700 hover:text-blue-600 block truncate max-w-[160px]">
                        {p.project_name}
                      </Link>
                    </td>
                    <td className="py-2.5 pr-4 text-sm text-gray-500 truncate max-w-[120px]">
                      {p.client?.company_name ?? "—"}
                    </td>
                    <td className="py-2.5">
                      <Badge status={p.status} map={PROJECT_BADGE} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {!freelance && (
        <Card
          title="Recent Jobs"
          action={<Link to="/jobs" className="text-sm text-blue-600 hover:underline font-medium">View all</Link>}
        >
          {loading ? (
            <table className="w-full"><tbody>{skeletonRows}</tbody></table>
          ) : recentJobs.length === 0 ? (
            <p className="text-sm text-gray-400">No jobs yet.</p>
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className={tableHeaderCls}>Job</th>
                  <th className={tableHeaderCls}>Vendor</th>
                  <th className={tableHeaderCls}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {recentJobs.map((j) => (
                  <tr key={j.id} className="hover:bg-gray-50 transition-colors">
                    <td className="py-2.5 pr-4 text-sm font-medium text-gray-700 truncate max-w-[160px]">
                      {j.name}
                    </td>
                    <td className="py-2.5 pr-4 text-sm text-gray-500 truncate max-w-[120px]">
                      {j.vendor?.company_name ?? "—"}
                    </td>
                    <td className="py-2.5">
                      <Badge status={j.status} map={JOB_BADGE} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
        )}
      </div>

      {/* ── Status breakdowns ── */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        <Card title="Project Status (recent 5)">
          {loading ? (
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse" />
          ) : Object.keys(projectBreakdown).length === 0 ? (
            <p className="text-sm text-gray-400">No data.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {Object.entries(projectBreakdown).map(([status, count]) => (
                <div key={status} className="flex items-center gap-1.5">
                  <Badge status={status} map={PROJECT_BADGE} />
                  <span className="text-sm font-semibold text-gray-700">{count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {!freelance && (
        <Card title="Job Status (recent 5)">
          {loading ? (
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse" />
          ) : Object.keys(jobBreakdown).length === 0 ? (
            <p className="text-sm text-gray-400">No data.</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {Object.entries(jobBreakdown).map(([status, count]) => (
                <div key={status} className="flex items-center gap-1.5">
                  <Badge status={status} map={JOB_BADGE} />
                  <span className="text-sm font-semibold text-gray-700">{count}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        )}
      </div>

      {/* ── Quick Actions ── */}
      <Card title="Quick Actions">
        <div className="flex flex-wrap gap-2">
          {[
            { to: "/projects/create-project",  icon: FiFolder,    label: "New Project"   },
            { to: "/clients/create-client",     icon: FiUsers,     label: "Add Client"    },
            // Enterprise-only quick actions are filtered out for freelance accounts.
            ...(freelance
              ? []
              : [
                  { to: "/vendors/create-vendor",   icon: FiUsers, label: "Add Vendor"  },
                  { to: "/managers/create-manager", icon: FiUsers, label: "Add Manager" },
                ]),
            { to: "/system-values",             icon: FiGrid,      label: "System Values" },
          ].map(({ to, icon: Icon, label }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600
                bg-gray-50 border border-gray-200 rounded-lg hover:bg-blue-50 hover:text-blue-700
                hover:border-blue-200 transition-colors"
            >
              <Icon size={14} />
              {label}
            </Link>
          ))}
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;
